import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/catch';
import type { ActionsObservable, Epic } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { deleteComponent, getComponentRelatedIssueCount } from '../services/delete.tsx';
import {
	DELETE_COMPONENT_REQUEST,
	DELETE_COMPONENT_SAFE,
	DELETE_COMPONENT_CONFIRMED,
	deleteComponentSuccess as deleteComponentSuccessAction,
	deleteComponentFail as deleteComponentFailAction,
	deleteComponentSafe as deleteComponentSafeAction,
	deleteComponentConfirmation as deleteComponentConfirmationAction,
} from '../state/actions/delete.tsx';
import type { Action } from '../state/actions/types.tsx';
import { onIssueCountFetched } from '../state/selectors/callbacks.tsx';
import { getComponentId } from '../state/selectors/context.tsx';
import type { State } from '../state/types.tsx';

const LOG_LOCATION = 'project-components.state.epics.delete';

const deleteComponentPerform = (moveIssuesTo: string | undefined, store: MiddlewareAPI<State>) => {
	const state = store.getState();
	const id = getComponentId(state);

	return Observable.fromPromise(deleteComponent(id, moveIssuesTo))
		.flatMap(() => Observable.of(deleteComponentSuccessAction()))
		.catch((error: FetchError) => {
			log.safeErrorWithoutCustomerData(LOG_LOCATION, error.message, error);
			return Observable.of(deleteComponentFailAction({ error }));
		});
};

// A request to delete the component
// Here we are checking whether it is safe to delete or requires user confirmation
export const deleteComponentRequest: Epic<Action, State> = (
	action$: ActionsObservable<Action>,
	store: MiddlewareAPI<State>,
) =>
	action$.ofType(DELETE_COMPONENT_REQUEST).mergeMap(() => {
		const id = getComponentId(store.getState());
		return Observable.fromPromise(getComponentRelatedIssueCount(id))
			.mergeMap((result): Observable<Action> => {
				const state = store.getState();
				onIssueCountFetched(state)(result.issueCount);

				if (result.issueCount === 0) {
					return Observable.of(deleteComponentSafeAction());
				}
				return Observable.of(deleteComponentConfirmationAction());
			})
			.catch((error: FetchError) => {
				log.safeErrorWithoutCustomerData(LOG_LOCATION, error.message, error);
				return Observable.of(deleteComponentFailAction({ error }));
			});
	});

// The component is safe to delete, just do it
export const deleteComponentSafe: Epic<Action, State> = (
	action$: ActionsObservable<Action>,
	store: MiddlewareAPI<State>,
) => action$.ofType(DELETE_COMPONENT_SAFE).mergeMap(() => deleteComponentPerform(undefined, store));

// The component deletion is confirmed by user, just do it
export const deleteComponentConfirmed: Epic<Action, State> = (
	action$: ActionsObservable<Action>,
	store: MiddlewareAPI<State>,
) =>
	action$
		.ofType(DELETE_COMPONENT_CONFIRMED)
		.mergeMap((action) => deleteComponentPerform(action.payload, store));
